import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AlertCircle } from 'lucide-react';
import { FeatureCard } from './FeatureCard';
import { fadeInUp, smoothTransition } from './animations';
import { AlertCircle as AlertIcon, Clock, TrendingDown, FileX, Frown } from 'lucide-react';

const problems = [
  {
    id: 'problem-indecision',
    icon: Frown,
    title: 'Clientes que no saben qué pedir',
    description: 'Indecisión que alarga el tiempo de pedido',
  },
  {
    id: 'problem-time',
    icon: Clock,
    title: 'Mesas que tardan demasiado en decidir',
    description: 'Pérdida de rotación y eficiencia',
  },
  {
    id: 'problem-sales',
    icon: TrendingDown,
    title: 'Productos que casi no se venden',
    description: 'Sin estrategia para destacar lo rentable',
  },
  {
    id: 'problem-update',
    icon: FileX,
    title: 'Menú difícil de actualizar',
    description: 'Cambios complicados y costosos',
  },
  {
    id: 'problem-modern',
    icon: AlertIcon,
    title: 'Experiencia que no se siente moderna',
    description: 'Clientes esperan tecnología',
  },
];

export const Problem = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const headerAnimation = useMemo(() => ({
    ...fadeInUp,
    animate: inView ? fadeInUp.animate : fadeInUp.initial,
  }), [inView]);

  const conclusionAnimation = useMemo(() => ({
    initial: { opacity: 0, scale: 0.95 },
    animate: inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 },
    transition: { duration: 0.6, delay: 0.4 },
  }), [inView]);

  return (
    <section ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      
      <div className="container px-4 sm:px-6 lg:px-8">
        <motion.div
          {...headerAnimation}
          transition={smoothTransition}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 border border-destructive/20 mb-6">
            <AlertCircle className="w-4 h-4 text-destructive" />
            <span className="text-sm font-medium text-destructive">El Problema Real</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Si hoy estás usando un menú en <span className="text-destructive">PDF</span> o uno <span className="text-destructive">físico</span>, estás perdiendo ventas todos los días sin darte cuenta.
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {problems.map((problem, index) => (
            <FeatureCard
              key={problem.id}
              icon={problem.icon}
              title={problem.title}
              description={problem.description}
              index={index}
              variant="destructive"
            />
          ))}
        </div>

        <motion.div
          {...conclusionAnimation}
          className="text-center"
        >
          <p className="text-xl sm:text-2xl text-muted-foreground font-medium">
            El problema no es tu comida... es <span className="text-primary font-bold">cómo la estás presentando</span>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
